import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CoatingWork, FileRecord } from '@twinsketch/topika-model';
import ErrorModal from 'src/components/common/ErrorModal';
import 'src/styles/workOrder/work-order.css';

interface WorkOrderCoatingWorkFormProps {
  coatingWorks: CoatingWork[];
  files: FileRecord[];
}

const WorkOrderCoatingWorkForm: React.FC<WorkOrderCoatingWorkFormProps> = ({
  coatingWorks,
  files,
}) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedIndices(prev => {
      const updated = new Set(prev);
      updated.has(index) ? updated.delete(index) : updated.add(index);
      return updated;
    });
  };

  return (
    <div className="content-wrapper">
      <ErrorModal message={error} onClose={() => setError(null)} />
      <h2>Coating Summary</h2>

      <div className="table-wrapper">
        <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Project</th>
              <th>Head Type</th>
              <th>Shim Spec</th>
              <th>Filter Pressure</th>
              <th>Die Pressure</th>
              <th>Slurry Type</th>
              <th>Solid Content (%)</th>
              <th>Foil Spec</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {coatingWorks.length > 0 ? (
              coatingWorks.map((work, index) => {
                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        <button
                          onClick={() => toggleExpand(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            color: 'brown',
                          }}
                        >
                          {expandedIndices.has(index) ? '−' : '+'}
                        </button>
                      </td>
                      <td>{work.date}</td>
                      <td>{work.projectName}</td>
                      <td>{work.coatingHeadType}</td>
                      <td>{work.shimSpec}</td>
                      <td>{work.filterPressureKgfCm2}</td>
                      <td>{work.dyePressureKgfCm2}</td>
                      <td>{work.slurryInformation.activeMaterialName}</td>
                      <td>{work.slurryInformation.solidContentPercent}</td>
                      <td>{work.foilInformation.foilSpec}</td>
                      <td>
                        {work.extractedFrom.sourceFileId ? (
                          <span style={{ color: 'green' }}>
                            {files.find(f => f.id === work.extractedFrom.sourceFileId)?.fileName ??
                              'File not found'}
                          </span>
                        ) : (
                          'No File'
                        )}
                      </td>
                    </tr>

                    {expandedIndices.has(index) && (
                      <tr>
                        <td colSpan={11}>
                          <div
                            style={{
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              borderTop: '1px solid #ccc',
                            }}
                          >
                            <strong>Coating Layers</strong>
                            <table style={{ width: '100%', marginBottom: '1rem' }}>
                              <thead>
                                <tr>
                                  <th>Side</th>
                                  <th>Length (m)</th>
                                  <th>Loading Spec</th>
                                  <th>Loading Min/Max</th>
                                  <th>Loading Tolerance</th>
                                  <th>Width Spec</th>
                                  <th>Width Min/Max</th>
                                  <th>Width Tolerance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {work.layers.map((layer, i) => (
                                  <tr key={i}>
                                    <td>{layer.side}</td>
                                    <td>{layer.coatingLength}</td>
                                    <td>{layer.targetLoading}</td>
                                    <td>
                                      {layer.minTargetLoading} / {layer.maxTargetLoading}
                                    </td>
                                    <td>{layer.targetLoadingTolerance}</td>
                                    <td>{layer.targetCoatingWidth}</td>
                                    <td>
                                      {layer.minTargetCoatingWidth} / {layer.maxTargetCoatingWidth}
                                    </td>
                                    <td>{layer.targetCoatingWidthTolerance}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <strong>Production Logs</strong>
                            <table style={{ width: '100%' }}>
                              <thead>
                                <tr>
                                  <th>Entry No</th>
                                  <th>Side</th>
                                  <th>Prod (m)</th>
                                  <th>Foil Feed (m)</th>
                                  <th>Init Viscosity</th>
                                  <th>Final Viscosity</th>
                                  <th>Pump RPM</th>
                                </tr>
                              </thead>
                              <tbody>
                                {work.productionLogs.map((log, logIndex) => (
                                  <tr key={logIndex}>
                                    <td>{log.entryNo}</td>
                                    <td>{log.coatingSide}</td>
                                    <td>{log.productionLength}</td>
                                    <td>{log.foilFeedLength}</td>
                                    <td>{log.initialViscosityCps}</td>
                                    <td>{log.finalViscosityCps}</td>
                                    <td>{log.pumpRpm ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', color: 'gray' }}>
                  No Coating Work Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderCoatingWorkForm;
